/*
 * The behaviour, without React.
 *
 * Everything the Next.js page does through Motion, Lenis and a handful of
 * client components, done here in plain DOM. Written so the page is complete
 * without it: the markup ships in its settled state, so with JS off or slow
 * every section is visible and every control works — this only adds the
 * motion, the haptics and the behaviours that need a scroll position or a
 * fetch.
 *
 * One listener per concern, all passive, and every scroll read inside a
 * requestAnimationFrame so nothing forces layout on the scroll thread.
 */
(function () {
  "use strict";

  var quiet = window.matchMedia("(prefers-reduced-motion: reduce)");
  var fine = window.matchMedia("(hover: hover) and (pointer: fine)");

  /* ── haptics ──────────────────────────────────────────────────────────
     Android Chrome implements the Vibration API; iOS Safari does not, and
     every workaround that claims otherwise relies on undocumented tricks that
     break between releases. iPhone gets the visual press and nothing more, by
     omission rather than by choice. Durations are tiny on purpose — past
     ~15ms it stops reading as a click and starts reading as the phone buzzing
     at you. */
  function buzz(pattern) {
    if (!("vibrate" in navigator)) return;
    try { navigator.vibrate(pattern); } catch (e) { /* not user-activated yet */ }
  }
  var tap = function () { buzz(8); };
  var brush = function () { buzz(4); };
  var hapticSuccess = function () { buzz([10, 55, 16]); };
  var hapticError = function () { buzz([14, 70, 14]); };

  /* Fired on pointerdown, not click: the vibration has to coincide with the
     finger landing or it reads as lag rather than as feedback. */
  var down = new WeakMap();
  document.addEventListener("pointerdown", function (e) {
    if (e.button !== 0 || !e.target || !e.target.closest) return;
    var el = e.target.closest("a,button,[role='button']");
    if (!el || down.get(el)) return;
    down.set(el, 1);
    (el.matches(".tactile-surface") ? brush : tap)();
  }, { passive: true });
  ["pointerup", "pointercancel", "pointerleave"].forEach(function (t) {
    document.addEventListener(t, function (e) {
      var el = e.target.closest && e.target.closest("a,button,[role='button']");
      if (el) down.delete(el);
    }, { passive: true });
  });
  document.addEventListener("keydown", function (e) {
    if (e.repeat || (e.key !== "Enter" && e.key !== " ")) return;
    if (document.activeElement && document.activeElement.matches("a,button,[role='button']")) tap();
  });

  /* ── reveals ──────────────────────────────────────────────────────────
     Matches components/drafts/v2/Rise.tsx: 40px over 900ms on the site's own
     curve, with a 6px blur lifting to zero — the blur is what reads as
     expensive, the difference between an element moving and one focusing.
     `fast` elements (the "In motion" heading + clips, per Rise's own `fast`
     prop) get half the duration and a viewport margin that starts them
     arriving rather than settling. The wipe variant (Rise's `wipe` prop) has
     no current caller on the page — the section it was for no longer exists —
     so it isn't ported here; add it back the same way if a future section
     needs it. */
  var rises = document.querySelectorAll("[data-rise]");
  if (!quiet.matches && "IntersectionObserver" in window && rises.length) {
    rises.forEach(function (el) {
      el.classList.add(el.hasAttribute("data-fast") ? "v2-rise-fast" : "v2-rise");
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.style.transitionDelay = (parseFloat(el.dataset.rise) || 0) + "ms";
        el.classList.add("v2-in");
        io.unobserve(el);
      });
    }, { rootMargin: "0px 0px -12% 0px" });
    var ioFast = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.style.transitionDelay = (parseFloat(el.dataset.rise) || 0) + "ms";
        el.classList.add("v2-in");
        ioFast.unobserve(el);
      });
    }, { rootMargin: "0px 0px 15% 0px" });
    rises.forEach(function (el) {
      (el.hasAttribute("data-fast") ? ioFast : io).observe(el);
    });
  } else if (quiet.matches) {
    rises.forEach(function (el) { el.style.opacity = "1"; });
  }

  /* ── the scroll progress hairline ─────────────────────────────────────
     scaleX on a compositor layer, never width — width is a layout property
     and animating it at scroll rate reflows the document every frame. */
  var bar = document.querySelector("[data-progress]");
  if (bar && !quiet.matches) {
    var pf = 0;
    var paint = function () {
      pf = 0;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var t = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      bar.style.transform = "scaleX(" + t + ")";
      bar.style.opacity = t > 0.004 ? "1" : "0";
    };
    paint();
    window.addEventListener("scroll", function () { if (!pf) pf = requestAnimationFrame(paint); }, { passive: true });
    window.addEventListener("resize", function () { if (!pf) pf = requestAnimationFrame(paint); }, { passive: true });
  }

  /* ── the header ───────────────────────────────────────────────────────
     Matches StickyHeader.tsx: always visible, on every viewport — it no
     longer hides on scroll down. It only gains a hairline + shadow once the
     page has moved past 8px, so at the very top it sits flush. */
  var header = document.querySelector("header");
  if (header) {
    var hf = 0;
    var readHeader = function () {
      hf = 0;
      var lifted = window.scrollY > 8;
      header.classList.toggle("border-transparent", !lifted);
      header.classList.toggle("border-aubergine/8", lifted);
      header.classList.toggle("shadow-[0_1px_20px_-12px_rgba(56,38,62,.5)]", lifted);
    };
    readHeader();
    window.addEventListener("scroll", function () { if (!hf) hf = requestAnimationFrame(readHeader); }, { passive: true });
  }

  /* ── the marks draw themselves in ────────────────────────────────────
     Matches BenefitIcons.tsx's Frame: each icon's strokes stagger by 80ms
     among themselves, and each icon starts 120ms after the previous one
     (plus BenefitIcon's own base offset) — grouped by icon, not by a flat
     index/8 split, since the peptides icon alone now draws 13 strokes and a
     flat split would run its own hub-and-spoke drawing across two "groups". */
  var marks = document.querySelector("[data-marks]");
  if (marks && "IntersectionObserver" in window) {
    var groups = [].slice.call(marks.children).map(function (item) {
      return [].slice.call(item.querySelectorAll("svg path, svg circle"));
    });
    if (!quiet.matches) {
      groups.forEach(function (strokes) {
        strokes.forEach(function (el) {
          el.setAttribute("pathLength", "1");
          el.style.strokeDasharray = "1 1";
          el.style.strokeDashoffset = "1";
          el.style.opacity = "0";
        });
      });
    }
    new IntersectionObserver(function (e, obs) {
      if (!e[0].isIntersecting) return;
      obs.disconnect();
      if (quiet.matches) return;
      groups.forEach(function (strokes, gi) {
        var groupDelay = 250 + gi * 120;
        strokes.forEach(function (el, i) {
          var delay = groupDelay + i * 80;
          el.style.transition = "stroke-dashoffset 1.1s cubic-bezier(.22,1,.36,1) " + delay + "ms, opacity .3s ease " + delay + "ms";
          el.style.strokeDashoffset = "0";
          el.style.opacity = "1";
          setTimeout(function () {
            el.style.strokeDasharray = "none";
            el.style.transition = "";
          }, 1100 + delay + 60);
        });
      });
    }, { threshold: 0.3, rootMargin: "0px 0px -10% 0px" }).observe(marks);
  }

  /* ── the range's mg counts up ─────────────────────────────────────────
     Matches Dose.tsx: runs once, on arrival, 1100ms cubic ease-out. The final
     value is already in the DOM (this only replaces it while counting), so a
     screen reader or no-JS visitor is never shown a number mid-climb. */
  var doses = document.querySelectorAll("[data-dose]");
  if (doses.length && !quiet.matches && "IntersectionObserver" in window) {
    doses.forEach(function (host) {
      var target = parseInt(host.getAttribute("data-dose"), 10);
      var valueEl = host.querySelector("[data-dose-value]");
      if (!target || !valueEl) return;
      var unit = (valueEl.textContent || "").replace(/[\d,]/g, "");
      new IntersectionObserver(function (e, obs) {
        if (!e[0].isIntersecting) return;
        obs.disconnect();
        var t0 = performance.now(), dur = 1100;
        var step = function (now) {
          var t = Math.min(1, (now - t0) / dur);
          var eased = 1 - Math.pow(1 - t, 3);
          valueEl.textContent = Math.round(target * eased).toLocaleString("en-GB") + unit;
          if (t < 1) requestAnimationFrame(step);
        };
        valueEl.textContent = "0" + unit;
        requestAnimationFrame(step);
      }, { threshold: 0.6 }).observe(host);
    });
  }

  /* ── the clips, on one beat ───────────────────────────────────────────
     Matches Reels.tsx's current 4s beat (this used to be 5s): each clip's
     playbackRate is duration/4 so a 4.0s, 3.6s and 4.0s file all fill the
     same 4-second window, and one interval returns them to zero together to
     mop up drift. Nothing is fetched until the row is near the fold,
     everything pauses when it leaves, and none of it starts under
     prefers-reduced-motion. */
  var reels = document.querySelector("[data-reels]");
  if (reels) {
    var clips = [].slice.call(reels.querySelectorAll("video"));
    clips.forEach(function (v) { v.muted = true; v.defaultMuted = true; v.playsInline = true; });
    var BEAT = 4;
    var beat = null;
    var restart = function () {
      clips.forEach(function (v) {
        if (v.duration && isFinite(v.duration)) v.playbackRate = v.duration / BEAT;
        v.currentTime = 0;
        v.play().catch(function () {});
      });
    };
    var halt = function () {
      if (beat) clearInterval(beat);
      beat = null;
      clips.forEach(function (v) { v.pause(); });
    };
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (e) {
        if (!e[0].isIntersecting) return halt();
        clips.forEach(function (v) { if (v.preload !== "auto") v.preload = "auto"; });
        if (quiet.matches || beat) return;
        var ready = clips.map(function (v) {
          return v.readyState >= 1 ? Promise.resolve() : new Promise(function (res) {
            v.addEventListener("loadedmetadata", function () { res(); }, { once: true });
          });
        });
        Promise.all(ready).then(function () {
          if (beat) return;
          restart();
          beat = setInterval(restart, BEAT * 1000);
        });
      }, { threshold: 0.3, rootMargin: "200px 0px" }).observe(reels);
    }
  }

  /* ── back to top ──────────────────────────────────────────────────────
     Matches BackToTop.tsx: shown past ~90% of one viewport height, a plain
     #top anchor that the smooth-scroll handler below already glides to. */
  var backToTop = document.querySelector("[data-back-to-top]");
  if (backToTop) {
    var bf = 0;
    var readBackToTop = function () {
      bf = 0;
      backToTop.setAttribute("data-shown", window.scrollY > window.innerHeight * 0.9 ? "true" : "false");
    };
    readBackToTop();
    window.addEventListener("scroll", function () { if (!bf) bf = requestAnimationFrame(readBackToTop); }, { passive: true });
    window.addEventListener("resize", function () { if (!bf) bf = requestAnimationFrame(readBackToTop); }, { passive: true });
  }

  /* ── the film ─────────────────────────────────────────────────────────
     A muted looping autoplay is allowed under WCAG only if it runs for under
     five seconds or can be paused; this one loops, so it gets a control. It
     also plays only while on screen, and never autoplays under reduced
     motion. `muted` is set as a property, not just an attribute: Safari
     rejects the play() promise when it thinks there is sound. */
  var film = document.querySelector("[data-film]");
  if (film && film.querySelector("video")) {
    var vid = film.querySelector("video");
    var filmBtn = film.querySelector("[data-film-toggle]");
    vid.muted = true; vid.defaultMuted = true; vid.playsInline = true;
    var mark = function () {
      var playing = !vid.paused;
      film.classList.toggle("is-playing", playing);
      if (filmBtn) {
        filmBtn.setAttribute("aria-pressed", playing ? "true" : "false");
        filmBtn.setAttribute("aria-label", playing ? "Pause the film" : "Play the film");
      }
    };
    vid.addEventListener("play", mark);
    vid.addEventListener("pause", mark);
    if (filmBtn) filmBtn.addEventListener("click", function () {
      if (vid.paused) vid.play().catch(function () {}); else vid.pause();
    });
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (e) {
        if (quiet.matches) return;
        if (e[0].isIntersecting) vid.play().catch(function () {}); else vid.pause();
      }, { threshold: 0.35 }).observe(vid);
    }
    mark();
  }

  /* ── magnetic controls ────────────────────────────────────────────────
     0.28 of the cursor's offset, clamped, with the inner label at 0.4 of the
     shell so the type leads the box very slightly. Fine pointers only: on
     touch there is no hover and the finger covers the control. */
  if (fine.matches && !quiet.matches) {
    document.querySelectorAll("[data-magnetic]").forEach(function (shell) {
      var max = +shell.dataset.magnetic || 5;
      var inner = shell.firstElementChild;
      shell.addEventListener("pointermove", function (e) {
        var r = shell.getBoundingClientRect();
        var x = Math.max(-max, Math.min(max, (e.clientX - (r.left + r.width / 2)) * 0.28));
        var y = Math.max(-max, Math.min(max, (e.clientY - (r.top + r.height / 2)) * 0.28));
        shell.style.transform = "translate3d(" + x + "px," + y + "px,0)";
        if (inner) inner.style.transform = "translate3d(" + x * 0.4 + "px," + y * 0.4 + "px,0)";
      });
      shell.addEventListener("pointerleave", function () {
        shell.style.transform = "";
        if (inner) inner.style.transform = "";
      });
    });

    /* ── tilt ──────────────────────────────────────────────────────────
       4.5 degrees; the glare that travels with the pointer does more of the
       work than the rotation. */
    document.querySelectorAll("[data-tilt]").forEach(function (box) {
      var max = +box.dataset.tilt || 4.5;
      var sheen = box.querySelector("[data-sheen]");
      box.addEventListener("pointermove", function (e) {
        var r = box.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        box.style.transform = "perspective(900px) rotateX(" + (-py * max).toFixed(2) + "deg) rotateY(" + (px * max).toFixed(2) + "deg)";
        if (sheen) {
          sheen.style.opacity = "1";
          sheen.style.background = "radial-gradient(38% 46% at " + ((px + 0.5) * 100).toFixed(1) + "% " + ((py + 0.5) * 100).toFixed(1) + "%, rgba(255,255,255,.28), rgba(255,255,255,0) 70%)";
        }
      });
      box.addEventListener("pointerleave", function () {
        box.style.transform = "";
        if (sheen) sheen.style.opacity = "0";
      });
    });
  }

  /* ── anchors ──────────────────────────────────────────────────────────
     One move that lands. The destination is re-read every frame, so the
     document growing under the scroll is absorbed into the curve instead of
     leaving the scroll short. The header is measured, not assumed. Exposed
     as glideTo so the waitlist gate below can reuse it for a "join" button
     that has already been pressed once (an already-joined visitor scrolling
     to the footer, same as the Next.js app's anchor-once-joined behaviour). */
  var glide = 0;
  var stopGlide = function () { if (glide) cancelAnimationFrame(glide); glide = 0; };
  ["wheel", "touchstart", "keydown"].forEach(function (t) {
    window.addEventListener(t, stopGlide, { passive: true });
  });
  function headerClearance() {
    var h = document.querySelector("header");
    return h && /sticky|fixed/.test(getComputedStyle(h).position)
      ? Math.round(h.getBoundingClientRect().height) + 12 : 88;
  }
  function glideTo(target) {
    var off = headerClearance();
    if (quiet.matches) { window.scrollTo(0, window.scrollY + target.getBoundingClientRect().top - off); return; }
    stopGlide();
    var startY = window.scrollY, t0 = performance.now(), dur = 900;
    var step = function (now) {
      var t = Math.min(1, (now - t0) / dur);
      var dest = window.scrollY + target.getBoundingClientRect().top - off;
      window.scrollTo(0, startY + (dest - startY) * (1 - Math.pow(1 - t, 3)));
      glide = t < 1 ? requestAnimationFrame(step) : 0;
    };
    glide = requestAnimationFrame(step);
  }
  document.addEventListener("click", function (e) {
    if (!e.target || !e.target.closest) return;
    var link = e.target.closest('a[href^="#"]');
    if (!link) return;
    var id = link.getAttribute("href");
    if (!id || id === "#") return;
    var target = document.querySelector(id);
    if (!target) return;
    e.preventDefault();
    glideTo(target);
  });

  /* ── the waitlist gate ────────────────────────────────────────────────
     Matches WaitlistGate.tsx + WaitlistModal.tsx — not in the theme before
     this rebuild. One shared "has this browser joined" flag in localStorage.
     The announcement bar, header-adjacent hero CTA, and any other
     [data-waitlist-trigger] all open the popup (and cancel its 5s timer)
     until joined, then glide-scroll to the footer form instead — the same
     protocol as the Next.js app's AnnouncementBar/HeroCta, done as a click
     handler here since Liquid can't know the joined state at render time.

     Both the popup and the footer form submit through Shopify's native
     customer form (see halo-waitlist.liquid / snippets/waitlist-popup.liquid)
     via fetch rather than a full page reload, so this gets the same
     spinner -> animated-checkmark flow as the Next.js app. Without JS the
     `{% form %}` tags still work as plain POSTs — this only intercepts them
     when it can. */
  var JOINED_KEY = "halo:waitlist:joined";
  var joined = false;
  try { joined = window.localStorage.getItem(JOINED_KEY) === "1"; } catch (e) { /* private mode */ }

  var popup = document.querySelector("[data-waitlist-popup]");
  var popupTimer = null;
  var popupDismissed = false;

  function markJoined() {
    joined = true;
    try { window.localStorage.setItem(JOINED_KEY, "1"); } catch (e) { /* can't persist, nothing to do */ }
    if (popupTimer) { clearTimeout(popupTimer); popupTimer = null; }
  }

  function openPopup() {
    if (!popup || joined) return;
    if (popupTimer) { clearTimeout(popupTimer); popupTimer = null; }
    popup.hidden = false;
    // rAF so the [hidden] -> flex swap has painted before the transition starts
    requestAnimationFrame(function () { popup.setAttribute("data-open", "true"); });
    document.body.style.overflow = "hidden";
    var input = popup.querySelector("input[type=email]");
    if (input) input.focus();
  }

  function closePopup() {
    if (!popup) return;
    popupDismissed = true;
    popup.removeAttribute("data-open");
    document.body.style.overflow = "";
    setTimeout(function () { popup.hidden = true; }, 300);
  }

  if (popup) {
    var closeBtn = popup.querySelector("[data-waitlist-popup-close]");
    if (closeBtn) closeBtn.addEventListener("click", closePopup);
    if (!joined) {
      popupTimer = setTimeout(function () {
        popupTimer = null;
        if (!popupDismissed) openPopup();
      }, 5000);
    }
  }

  document.querySelectorAll("[data-waitlist-trigger]").forEach(function (btn) {
    btn.addEventListener("click", function () {
      if (joined) {
        var target = document.querySelector("#waitlist");
        if (target) glideTo(target);
        return;
      }
      openPopup();
    });
  });

  /* AJAX submit, shared by the footer form and the popup form. Shopify's
     customer form doesn't return structured JSON, so a 2xx response is
     treated as success — this can't distinguish "already subscribed" from a
     clean signup the way the Next.js app's /api/waitlist JSON response can,
     which is an accepted trade-off of using Shopify's native form instead of
     a serverless backend the theme doesn't have. */
  function wireWaitlistForm(form) {
    if (!form) return;
    var idle = form.querySelector("[data-waitlist-idle]");
    var done = form.querySelector("[data-waitlist-done]");
    var label = form.querySelector("[data-waitlist-label]");
    var spinner = form.querySelector("[data-waitlist-spinner]");
    var submitBtn = form.querySelector("[data-waitlist-submit]");
    var errorEl = form.querySelector("[data-waitlist-error]");
    var emailEl = form.querySelector("[data-waitlist-email]");
    var sending = false;

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (sending) return;
      sending = true;
      if (errorEl) {
        errorEl.textContent = "";
        errorEl.classList.remove("mt-3", "max-h-12", "opacity-100");
        errorEl.classList.add("mt-0", "max-h-0", "opacity-0");
      }
      if (label) label.style.opacity = "0";
      if (spinner) { spinner.classList.remove("hidden"); spinner.classList.add("flex"); }
      if (submitBtn) submitBtn.disabled = true;

      var emailInput = form.querySelector("input[type=email]");
      var email = emailInput ? emailInput.value : "";

      fetch(form.action, { method: "POST", body: new FormData(form) })
        .then(function (res) {
          sending = false;
          if (submitBtn) submitBtn.disabled = false;
          if (label) label.style.opacity = "1";
          if (spinner) { spinner.classList.add("hidden"); spinner.classList.remove("flex"); }

          if (!res.ok) throw new Error("bad status");

          hapticSuccess();
          markJoined();
          if (emailEl) emailEl.textContent = email;
          if (idle) idle.hidden = true;
          if (done) { done.hidden = false; done.focus && done.focus(); }
        })
        .catch(function () {
          sending = false;
          if (submitBtn) submitBtn.disabled = false;
          if (label) label.style.opacity = "1";
          if (spinner) { spinner.classList.add("hidden"); spinner.classList.remove("flex"); }
          hapticError();
          if (errorEl) {
            errorEl.textContent = "Something went wrong. Please try again.";
            errorEl.classList.remove("mt-0", "max-h-0", "opacity-0");
            errorEl.classList.add("mt-3", "max-h-12", "opacity-100");
          }
        });
    });
  }

  wireWaitlistForm(document.getElementById("halo-waitlist"));
  wireWaitlistForm(document.getElementById("halo-waitlist-popup"));
})();
